import { fireEvidenceQuotaConsumedToast } from '@/components/chat/components/fireEvidenceQuotaConsumedToast';
import { ADVANCED_EVIDENCE_IN_SESSION_QUOTA } from '@/hooks/useEvidenceInSessionLimits';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { endOfISOWeek } from 'date-fns';
import { useCallback } from 'react';

interface EvidenceQuotaToastDevSectionProps {
  /** Called after the user triggers an action that should close the dev-tools sheet. */
  onRequestClose?: () => void;
}

interface ToastChip {
  testId: string;
  label: string;
  isPracticeUpgradeTarget: boolean;
  remaining: number;
}

/**
 * Builds the chip configurations covering Figma's Clinician
 * (10492:57298 consume / 10492:57626 exhausted) and Practice
 * (10742:53227 consume; exhausted mirrors the Clinician design) variants in
 * the three states the design specifies (5 left, 1 left, exhausted). Kept
 * outside the component to make the layout obvious.
 */
const TOAST_CHIPS: ReadonlyArray<ToastChip> = [
  {
    testId: 'dev-tools-evidence-quota-toast-clinician-5-left',
    label: 'Clinician — 5 left',
    isPracticeUpgradeTarget: false,
    remaining: 5,
  },
  {
    testId: 'dev-tools-evidence-quota-toast-clinician-1-left',
    label: 'Clinician — 1 left',
    isPracticeUpgradeTarget: false,
    remaining: 1,
  },
  {
    testId: 'dev-tools-evidence-quota-toast-clinician-exhausted',
    label: 'Clinician — exhausted',
    isPracticeUpgradeTarget: false,
    remaining: 0,
  },
  {
    testId: 'dev-tools-evidence-quota-toast-practice-5-left',
    label: 'Practice — 5 left',
    isPracticeUpgradeTarget: true,
    remaining: 5,
  },
  {
    testId: 'dev-tools-evidence-quota-toast-practice-1-left',
    label: 'Practice — 1 left',
    isPracticeUpgradeTarget: true,
    remaining: 1,
  },
  {
    testId: 'dev-tools-evidence-quota-toast-practice-exhausted',
    label: 'Practice — exhausted',
    isPracticeUpgradeTarget: true,
    remaining: 0,
  },
];

/**
 * Dev-tools sidebar section for the post-consume in-session evidence quota
 * System Toast. Fires the toast directly via `fireEvidenceQuotaConsumedToast`
 * — no Jotai override or BE round-trip — so engineers can iterate on copy
 * and tier colours without running a real Evidence query.
 *
 * Renders six chips covering both tiers (Clinician `10492:57298` consume /
 * `10492:57626` exhausted; Practice `10742:53227` consume — exhausted mirrors
 * the Clinician design) in the three states the design specifies (5 left,
 * 1 left, exhausted). Reset date is derived from `endOfISOWeek(new Date())`
 * so the format matches what the BE emits in production (Sunday 00:00 UTC
 * anchor).
 */
export function EvidenceQuotaToastDevSection({
  onRequestClose,
}: Readonly<EvidenceQuotaToastDevSectionProps>) {
  const handleFire = useCallback(
    (chip: ToastChip) => {
      const advancedUsed = Math.max(
        0,
        ADVANCED_EVIDENCE_IN_SESSION_QUOTA - chip.remaining
      );
      fireEvidenceQuotaConsumedToast({
        advancedUsed,
        currentPeriodEnd: endOfISOWeek(new Date()).toISOString(),
        isPracticeUpgradeTarget: chip.isPracticeUpgradeTarget,
      });
      onRequestClose?.();
    },
    [onRequestClose]
  );

  return (
    <section aria-labelledby="dev-tools-evidence-quota-toast-heading" className="mt-4">
      <TypographyP2SemiBold id="dev-tools-evidence-quota-toast-heading" className="mb-2">
        Evidence quota toast
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-3 text-text-tertiary">
        Fires the post-consume in-session evidence quota System Toast directly. Bypasses
        the SSE chunk and the launch flag so the toast is always rendered — useful for
        iterating on copy and tier colours. Figma:{' '}
        <span className="font-mono">10492:57298</span> Clinician /{' '}
        <span className="font-mono">10492:57626</span> Clinician exhausted /{' '}
        <span className="font-mono">10742:53227</span> Practice.
      </TypographyCaption>
      <div className="grid grid-cols-2 gap-2">
        {TOAST_CHIPS.map((chip) => (
          <Button
            key={chip.testId}
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => handleFire(chip)}
            data-testid={chip.testId}
          >
            {chip.label}
          </Button>
        ))}
      </div>
    </section>
  );
}
